import { Component, DestroyRef, OnInit } from '@angular/core';
import { combineLatest, map, mergeMap, Observable, of, switchMap, take, tap } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { LocationSelectors } from '@states/location/location.selector-types';
import { MenuKey } from '@enums/menu.enum';
import * as MenuSelectors from '@states/menu/menu.selectors';
import * as SharedSelectors from '@states/shared/shared.selectors';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { LocationEditActions } from '@states/location-edit/location-edit.action-types';
import * as MenuActions from '@states/menu/menu.actions';
import { locationEditMenuLevel2 } from '@consts/menu.const';
import { LocationEditSelectors } from '@states/location-edit/location-edit.selector-types';
import { routerSegments } from '@consts/routes';
import { DeviceModel } from '@models/device.model';
import { CanComponentDeactivate } from '../../core/can-deactivate.guard';
import { ConfirmDialogService } from '../../shared/confirm-dialog/confirm-dialog.service';
import { AppleTvSelectors } from '@states/apple-tv/apple-tv.selector-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { ExternalStorageSelectors } from '@states/external-storage/external-storage.selector-types';
import { RemoteIOSelectors } from '@states/remote-io/remote-io.selector-types';
import { UiBreadCrumbItem } from '@models/route.models';
import { BreadcrumbsService } from 'src/app/services/breadcrumbs.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  UiHeaderWithBreadcrumbsComponent
} from '../../shared/ui-kit/ui-header-with-breadcrumbs/ui-header-with-breadcrumbs.component';
import { LetDirective, PushPipe } from '@ngrx/component';
import { UiKitModule } from '../../shared/ui-kit/ui-kit.module';
import { AsyncPipe, NgIf } from '@angular/common';
import { UiMenuLevel2Component } from '../../shared/ui-kit/ui-menu-level2/ui-menu-level2.component';
import { UiButtonComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-edit-location',
  templateUrl: './edit-location.component.html',
  styleUrls: ['./edit-location.component.scss'],
  imports: [
    UiHeaderWithBreadcrumbsComponent,
    LetDirective,
    UiKitModule,
    PushPipe,
    AsyncPipe,
    TranslatePipe,
    NgIf,
    RouterOutlet,
    UiButtonComponent,
    UiMenuLevel2Component,
  ],
})
export class EditLocationComponent implements OnInit, CanComponentDeactivate {
  public breadCrumbs: UiBreadCrumbItem[] = [];
  public selectActiveLevel2$: Observable<MenuKey> = this.store$.pipe(
    select(MenuSelectors.selectActiveLevel2),
  );
  public selectIsSaving$: Observable<boolean> = this.store$.pipe(select(SharedSelectors.selectIsSaving));

  public selectDeviceState$: Observable<DeviceModel.DeviceState> = this.store$.pipe(
    select(LocationEditSelectors.selectDeviceState),
  );
  public selectIsFormValid$: Observable<boolean> = this.store$.pipe(
    select(AppleTvSelectors.selectIsFormValid),
  );
  public selectIsSavingAppleTV$: Observable<boolean> = this.store$.pipe(
    select(AppleTvSelectors.selectIsSaving),
  );
  public selectIsSavingExternalStorage: Observable<boolean> = this.store$.pipe(
    select(ExternalStorageSelectors.selectIsSaving),
  );
  public selectIsExternalStorageFormValid$: Observable<boolean> = this.store$.pipe(
    select(ExternalStorageSelectors.selectIsFormValid),
  );
  public selectIsSavingRemoteIO: Observable<boolean> = this.store$.pipe(
    select(RemoteIOSelectors.selectIsSaving),
  );
  public selectIsRemoteIOFormValid$: Observable<boolean> = this.store$.pipe(
    select(RemoteIOSelectors.selectIsFormValid),
  );
  public selectIsLocationDetailsFormValid$: Observable<boolean> = this.store$.pipe(
    select(LocationEditSelectors.selectLocationDetailsIsFormValid),
  );

  public isDeveloper$: Observable<boolean> = this.store$.select(OrganizationSelectors.isDeveloper);

  public locationId: string;
  public menuKeys = MenuKey;

  private isDeactivating = false;
  b;

  constructor(
    private store$: Store,
    private router: Router,
    private confirm: ConfirmDialogService,
    private breadcrumbsService: BreadcrumbsService,
    private route: ActivatedRoute,
    private translationService: TranslateService,
    private destroyRef: DestroyRef,
  ) {}

  canDeactivate(): Observable<boolean> {
    return this.store$.select(LocationEditSelectors.selectCanDeactivate).pipe(
      take(1),
      switchMap((canDeactivate) => {
        this.isDeactivating = canDeactivate;

        if (this.isDeactivating) {
          return of(true);
        }

        return this.confirm.discard().pipe(
          tap((result) => {
            this.isDeactivating = result;
          }),
        );
      }),
    );
  }

  ngOnInit(): void {
    this.store$.dispatch(LocationEditActions.resetToInitialState());
    this.store$.dispatch(
      MenuActions.setActiveLevel2Menu({
        activeLevel2: MenuKey.details,
      }),
    );

    this.route.params
      .pipe(
        take(1),
        mergeMap((res) => {
          return combineLatest([
            of(res),
            this.store$.pipe(select(LocationSelectors.selectLocationById(res['locationId'])), take(1)),
          ]);
        }),
        tap(([res, location]) => {
          const breadCrumbs = [
            { name: this.translationService.instant('MAIN_MENU.CAMERAS'), route: '/location-v2' },
            { name: location.name },
            {
              name: this.translationService.instant('LOCATION_LIST.BUTTONS.EDIT_LOCATION'),
              route: `/${routerSegments.editLocation}/${res['locationId']}/details`,
            },
          ];

          this.breadcrumbsService.updateBreadcrumb(this.route, this.router, breadCrumbs);
        }),

        map(([res, location]) => location),
      )
      .subscribe((location) => {
        this.locationId = location._id;
        this.store$.dispatch(LocationEditActions.setSelectedLocationId({ selectedLocationId: location._id }));
        this.store$.dispatch(LocationEditActions.setSelectedLocation({ selectedLocation: location }));
      });
    this.isDeveloper$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      const menu = locationEditMenuLevel2.filter((menu) => {
        return menu.isDeveloper === res || !menu.isDeveloper;
      });
      this.store$.dispatch(
        MenuActions.setLevel2Menu({
          level2Menu: menu,
        }),
      );
    });
  }

  public get isCreate() {
    return this.router.url.includes(routerSegments.create);
  }

  public save() {
    this.store$.dispatch(LocationEditActions.pressSave());
  }

  public create() {
    this.store$.dispatch(LocationEditActions.pressCreate());
  }

  public addSpeaker() {
    this.router.navigate([routerSegments.addSpeaker], { relativeTo: this.route });
  }
}
